import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Link,
  TextField,
  Typography,
} from '@material-ui/core';
import { Form, Formik, FormikHelpers, FormikProps } from 'formik';
import { Dispatch, FunctionComponent, SetStateAction, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import * as yup from 'yup';
import { initialiseHcpUser, makeFormErrors, signIn } from '../../api/AuthApi';
import buttonArrow from '../../assets/svgs/arrow-right.svg';
import { AlertBar } from '../../components/AlertBar';
import { CONNECT_SCREENS, USER_TYPE } from '../../constants';
import { isNonEmptyString } from '../../helpers/StringUtils';
import { Analytics } from '../../helpers/analytics/Analytics';
import { AnalyticsEventName } from '../../helpers/analytics/AnalyticsEvent';
import { useAuthDispatch, useAuthStore } from '../../store/AuthStore';
import { ActionType } from '../../store/StoreTypes';
import { ValidationMessages } from './types';

const SCHEMA: yup.ObjectSchema<Password> = yup
  .object()
  .shape({
    password: yup.string().required(ValidationMessages.PASSWORD_REQUIRED),
  })
  .required();

type Password = {
  password: string;
};

type PasswordScreenProps = {
  setStep: Dispatch<SetStateAction<number>>;
  setPassword: Dispatch<SetStateAction<string>>;
  email: string;
  skipRegistrationStep: boolean;
};

export const PasswordScreen: FunctionComponent<PasswordScreenProps> = (props) => {
  const { email } = props;

  const hcpReferralHash = useAuthStore((state) => state.hcpReferralHash);
  const hasHcpReferralHash = isNonEmptyString(hcpReferralHash);
  const [errorMessage, setErrorMessage] = useState<string>();
  const history = useHistory();
  const dispatch = useAuthDispatch();

  useEffect(function trackAnalytics() {
    Analytics.track({
      event: AnalyticsEventName.h_screen_viewed,
      screen: CONNECT_SCREENS.PASSWORD,
    });
  }, []);

  const initialValues: Password = { password: '' };

  async function onSubmit(values: Password, helpers: FormikHelpers<Password>) {
    try {
      if (props.skipRegistrationStep) {
        const firebaseUser = await signIn(email, values.password);
        if (firebaseUser) {
          Analytics.identifyLoggedInUser({
            userId: firebaseUser?.uid,
            email,
            hcpProduct: true,
          });
        }

        Analytics.track({
          event: AnalyticsEventName.a_login_succeeded,
          userType: hasHcpReferralHash ? USER_TYPE.PATIENT : USER_TYPE.HCP,
        });

        if (!hasHcpReferralHash) {
          const user = await initialiseHcpUser();
          dispatch({ type: ActionType.SET_USER, user });
        }

        if (hasHcpReferralHash) {
          try {
            history.push('/users/patient/consent');
          } catch (error) {
            setErrorMessage(`Unable to log-in, please try again ${error}`);
          }
        } else {
          history.push('/');
        }
      } else {
        dispatch({
          type: ActionType.SET_AUTHENTICATING_EXISTING_MINDSET_ID,
          authenticatingExistingMindsetId: true,
        });

        const firebaseUser = await signIn(email, values.password);
        if (firebaseUser) {
          Analytics.identifyLoggedInUser({
            userId: firebaseUser?.uid,
            email,
            hcpProduct: false,
          });
        }

        Analytics.track({
          event: AnalyticsEventName.a_mindset_authentication_succeeded,
        });
        props.setPassword(values.password);
        props.setStep(2);
      }
    } catch (error) {
      handleErrors(error, helpers);
    }
  }

  function handleErrors(error: any, { setErrors }: FormikHelpers<Password>) {
    const formErrors = makeFormErrors(error);
    if (formErrors !== undefined) {
      setErrors(formErrors as Password);
    } else {
      setErrorMessage('Unable to log-in, please try again.');
      if (props.skipRegistrationStep) {
        Analytics.track({
          event: AnalyticsEventName.a_login_failed,
          error,
          userType: hasHcpReferralHash ? USER_TYPE.PATIENT : USER_TYPE.HCP,
        });
      } else {
        Analytics.track({
          event: AnalyticsEventName.a_mindset_authentication_failed,
          error,
        });
      }
    }
  }

  return (
    <Grid container direction="column" data-testid="password-screen">
      <AlertBar
        copy={errorMessage}
        status="error"
        open={isNonEmptyString(errorMessage)}
        onClose={(isOpen) => !isOpen && setErrorMessage(undefined)}
      />

      <Formik
        initialValues={initialValues}
        validationSchema={SCHEMA}
        validateOnBlur={false}
        onSubmit={onSubmit}
      >
        {(formikProps: FormikProps<Password>) => {
          const { values, touched, dirty, errors, handleBlur, handleChange, isSubmitting } =
            formikProps;

          return (
            <Form>
              <Grid container item direction="column">
                <Box width="100%" textAlign="center" marginBottom="40px">
                  <TextField
                    required
                    fullWidth
                    placeholder="password"
                    name="password"
                    id="password"
                    value={values.password}
                    type="password"
                    onChange={handleChange}
                    onBlur={handleBlur}
                    helperText={
                      isNonEmptyString(errors.password) && touched.password && errors.password
                    }
                    error={!!(isNonEmptyString(errors.password) && touched.password)}
                    inputProps={{ style: { textAlign: 'center' } }}
                  />
                </Box>

                <Grid container item justifyContent="center">
                  <Box width="200px" display="flex" position="relative">
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      size="large"
                      fullWidth
                      disabled={!dirty || isSubmitting}
                      endIcon={
                        <img src={buttonArrow} alt="next button" width="24px" height="24px" />
                      }
                    >
                      Login
                    </Button>
                    {isSubmitting && <CircularProgress size={24} color="primary" />}
                  </Box>
                </Grid>
              </Grid>
            </Form>
          );
        }}
      </Formik>
      <Box textAlign="center" marginTop="50px">
        <Typography>
          Forgot your password?
          <Button
            display="inline"
            component={Link}
            color="primary"
            onClick={() => props.setStep(3)}
            underline="none"
          >
            Reset
          </Button>
        </Typography>
      </Box>
    </Grid>
  );
};
